import { useEffect, useId, useState, type InputHTMLAttributes } from 'react';
import { formatTimeAs, parseTime, useTime24 } from '../lib/time';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  /** "HH:MM", or "" for none. */
  value: string;
  onChange: (value: string) => void;
};

/**
 * A time box that takes 12-hour or 24-hour typing ("4:30 pm", "430p", "16:30", "1630") and
 * shows the time the way this device is set to. Unlike the browser's time picker, it never hides am/pm.
 */
export function TimeInput({ value, onChange, className, onBlur, ...rest }: Props & { invalid?: boolean }) {
  const h24 = useTime24();
  const shown = value ? formatTimeAs(value, h24) : '';
  const [text, setText] = useState(shown);
  const [focused, setFocused] = useState(false);
  const [bad, setBad] = useState(false);
  useEffect(() => {
    if (!focused) setText(shown);
  }, [shown, focused]);
  const { invalid, ...inputProps } = rest as typeof rest & { invalid?: boolean };
  return (
    <input
      type="text"
      inputMode="text"
      autoComplete="off"
      spellCheck={false}
      placeholder={h24 ? '16:30' : '4:30 pm'}
      className={`field-input tnum ${bad || invalid ? 'border-danger' : ''} ${className ?? ''}`}
      aria-invalid={bad || invalid}
      title="Type a time, like 4:30 pm or 16:30"
      value={text}
      onFocus={() => setFocused(true)}
      onChange={(e) => {
        setText(e.target.value);
        const t = parseTime(e.target.value);
        if (t) {
          setBad(false);
          if (t !== value) onChange(t);
        } else if (!e.target.value.trim()) {
          setBad(false);
          if (value) onChange('');
        }
      }}
      onBlur={(e) => {
        setFocused(false);
        const t = parseTime(text);
        if (t) {
          setText(formatTimeAs(t, h24));
          setBad(false);
        } else setBad(!!text.trim());
        onBlur?.(e);
      }}
      {...inputProps}
    />
  );
}

export function TimeField({ label, error, hint, id, ...rest }: Props & { label: string; error?: string; hint?: string }) {
  const auto = useId();
  const fid = id ?? auto;
  const [touchedBad, setTouchedBad] = useState(false);
  return (
    <div className="space-y-1.5">
      <label htmlFor={fid} className="label-caps block">
        {label}
      </label>
      <TimeInput id={fid} invalid={!!error} onBlur={(e) => setTouchedBad(!!e.target.value.trim() && !parseTime(e.target.value))} {...rest} />
      {error ? <p className="text-[13px] text-danger">{error}</p> : touchedBad ? <p className="text-[13px] text-danger">Type a time like 4:30 pm or 16:30.</p> : hint ? <p className="text-[13px] text-muted">{hint}</p> : null}
    </div>
  );
}
